import _ from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Promise, { OperationalError } from 'bluebird';
import { Polyline, InfoWindow } from '@react-google-maps/api';
import { polylineOptions } from '../../app/google_maps';

// Do not round to hour or day. Default is to round at 45 min/22 hours
// https://momentjs.com/docs/#/customization/relative-time-threshold/
moment.relativeTimeThreshold('m', 60);
moment.relativeTimeThreshold('h', 24);

const colorGrey = '#7F8C8D';
const directionsService = new google.maps.DirectionsService();

const directionPromises = [];
function getDirections(jt) {
  const direction = _.find(directionPromises,
    ({ journeyTemplate }) => _.isEqual(journeyTemplate, jt));
  if (direction) { return direction.promise; }
  const promise = new Promise((resolve, reject) => {
    directionsService.route({
      origin: new google.maps.LatLng(jt.stops[0].lat, jt.stops[0].lng),
      destination: new google.maps.LatLng(_.last(jt.stops).lat, _.last(jt.stops).lng),
      waypoints: _.slice(jt.stops, 1, -1).map(s =>
        ({ location: new google.maps.LatLng(s.lat, s.lng), stopover: true })),
      travelMode: 'DRIVING',
    }, (response, status) => {
      if (status !== 'OK') { return reject(new OperationalError('Failed to get directions')); }
      return resolve(response.routes[0].legs);
    });
  });
  directionPromises.push({ journeyTemplate: jt, promise });
  return promise;
}

function pathFromLeg(leg) {
  const segments = [];
  _.forEach(leg.steps, ({ path }) => {
    _.forEach(path, segment => segments.push(segment));
  });
  return segments;
}

// Valhalla encodes shapes with the Google polyline algorithm but at precision 1e6,
// so `google.maps.geometry.encoding.decodePath` (hardcoded to 1e5) cannot be used.
/* eslint-disable no-bitwise */
function decodeShape(encoded, precision = 6) {
  const factor = 10 ** precision;
  const path = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    let byte = 0;
    let shift = 0;
    let result = 0;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);

    shift = 0;
    result = 0;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);

    path.push(new google.maps.LatLng(lat / factor, lng / factor));
  }
  return path;
}
/* eslint-enable no-bitwise */

function latWithOffset(latLng, zoom) {
  const offset = 10 ** -Math.max(3, zoom - 10);
  return latLng.lat() + offset;
}

export const journeyTemplateShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  stops: PropTypes.arrayOf(PropTypes.shape({
  })).isRequired,
  leg_geometries: PropTypes.arrayOf(PropTypes.string),
});

export default class JourneyTemplatePolyline extends Component {
  constructor(props) {
    super(props);
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onMouseOver = this.onMouseOver.bind(this);
    this.onMouseOut = _.debounce(this.onMouseOut.bind(this), 1000);
  }

  componentDidMount() {
    this.loadLegPaths(this.props.journeyTemplate);
  }

  componentWillReceiveProps(nextProps) {
    if (!_.isEqual(this.props.journeyTemplate, nextProps.journeyTemplate)) {
      this.loadLegPaths(nextProps.journeyTemplate);
    }

    if (!nextProps.selected) {
      this.unsubscribeEvents();
    } else if (nextProps.selected) {
      this.setupLegHover();
    }
  }

  componentWillUnmount() {
    if (this.promise) { this.promise.cancel(); }
    this.unsubscribeEvents();
  }

  onMouseOver({ latLng }) {
    this.onMouseOut.cancel();
    this.setState({
      hover: true,
      position: new google.maps.LatLng(latWithOffset(latLng, this.props.zoom), latLng.lng()),
    });
  }

  onMouseMove({ latLng }) {
    this.onMouseOut.cancel();
    this.setState({
      hover: true,
      position: new google.maps.LatLng(latWithOffset(latLng, this.props.zoom), latLng.lng()),
    });
  }

  onMouseOut() {
    this.setState({ hover: false });
  }

  onCloseClick() {
    this.onMouseOut.cancel();
    this.setState({ hover: false });
  }

  setupLegHover() {
    const $legs = $('.journey-template-route-leg');
    $legs.off(this.mouseEnterEvent()).on(this.mouseEnterEvent(), ({ currentTarget }) => {
      const $tr = $(currentTarget);
      this.setState({ selectedLeg: $legs.index($tr) });
    });
    $legs.off(this.mouseLeaveEvent()).on(this.mouseLeaveEvent(), () => {
      this.setState({ selectedLeg: null });
    });
  }

  // One path per leg, i.e. per consecutive pair of journey template stops. Stored Valhalla
  // geometries already follow that layout, so they line up with `hlp_stop_indices` exactly
  // like the legs returned by the Directions service do.
  loadLegPaths(journeyTemplate) {
    if (this.promise) { this.promise.cancel(); }

    const geometries = _.compact(journeyTemplate.leg_geometries);
    if (!_.isEmpty(geometries)) {
      this.promise = null;
      this.setState({ legPaths: _.map(geometries, geometry => decodeShape(geometry)) });
      return;
    }

    this.promise = new Promise((resolve, reject) => getDirections(journeyTemplate)
      .then(directions => resolve(directions))
      .catch(e => reject(e)),
    ).then((directions) => {
      this.setState({ legPaths: _.map(directions, pathFromLeg) });
    });
  }

  distance() {
    const distance = this.props.journeyTemplate.distance;
    return distance >= 1000 ? `${(distance / 1000).toFixed(2)} km` : `${distance} m`;
  }

  unsubscribeEvents() {
    $('.journey-template-route-leg').off(this.mouseEnterEvent()).off(this.mouseLeaveEvent());
    this.setState({ selectedLeg: null });
  }

  durationText() {
    const { journeyTemplate } = this.props;
    const duration = moment.duration(journeyTemplate.duration * 1000);
    const hours = moment.duration(duration.hours(), 'hours');
    const minutes = moment.duration(duration.minutes(), 'minutes');
    return _([hours, minutes]).filter(d => d.as('seconds') > 0)
      .map(d => d.humanize())
      .join(', ');
  }

  infoWindow() {
    if (!this.state.hover) { return null; }
    return (
      <InfoWindow
        onCloseClick={this.onCloseClick}
        position={this.state.position}
      >
        <div className="info-journey" style={{ width: 70 }}>
          <div className="info-journey-distance">{this.distance()}</div>
          <div className="info-journey-time">
            {this.durationText()}
          </div>
        </div>
      </InfoWindow>
    );
  }

  legs() {
    const { journeyTemplate, onClick } = this.props;
    return _.map(this.state.legPaths, (path, i) => (
      <Polyline
        key={i}
        options={_.extend(
          {},
          polylineOptions,
          {
            strokeColor: this.color(i),
            strokeWeight: this.state.selectedLeg === i ? 8 : 5,
            strokeOpacity: this.state.selectedLeg === i ? 0.75 : 0.5,
          },
        )}
        onClick={() => { onClick(journeyTemplate); }}
        path={path}
        onMouseOver={this.onMouseOver}
        onMouseOut={this.onMouseOut}
      />
    ));
  }

  color(stopIndex) {
    if (_.includes(this.props.journeyTemplate.hlp_stop_indices, stopIndex)) { return colorGrey; }
    return this.props.journeyTemplate.color;
  }

  mouseEnterEvent() {
    return `mouseenter.journey_template_${this.props.journeyTemplate.id}`;
  }

  mouseLeaveEvent() {
    return `mouseleave.journey_template_${this.props.journeyTemplate.id}`;
  }

  render() {
    if (!this.state || !this.state.legPaths) { return null; }

    return (
      <div>
        {this.legs()}
        {this.infoWindow()}
      </div>
    );
  }
}

JourneyTemplatePolyline.propTypes = {
  journeyTemplate: journeyTemplateShape.isRequired,
  zoom: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  selected: PropTypes.bool.isRequired,
};
